import type { EstadoOnboarding } from '@pasos/schemas';
import { type FormEvent, useState } from 'react';
import { CampoGrande } from '../components/domain/CampoGrande.js';
import { Boton } from '../components/ui/Boton.js';
import { ArrowRight, CalendarCheck, ICONO, MessageCircle } from '../components/ui/iconos.js';
import { PACIENTE } from '../copy/index.js';
import { Stepper } from './Stepper.js';

type PasoIdentidadProps = {
  estado: EstadoOnboarding;
  onConfirmar: (cirugiaAt: string) => void;
  enviando: boolean;
  error?: string | undefined;
};

/**
 * P-02a · el paciente confirma lo que el equipo médico ya cargó.
 *
 * Confirma, no completa: los datos vienen pre-cargados del alta. Un adulto mayor
 * recién operado tipeando su fecha de cirugía en un celular es una fuente de
 * errores que además no aporta nada — el dato ya está en el sistema.
 *
 * El saludo de PIA abre el flujo porque es su primera aparición y conviene que
 * llegue como alguien que acompaña, no como un formulario.
 *
 * E3.8.2, contra la referencia P-02a: PIA se presenta con cara y globo, y los
 * campos pasan a 60 px con foco navy visible.
 */
export function PasoIdentidad({ estado, onConfirmar, enviando, error }: PasoIdentidadProps) {
  const [cirugiaAt, setCirugiaAt] = useState(estado.cirugiaAt ?? '');
  const [tocado, setTocado] = useState(false);

  const faltaFecha = tocado && !cirugiaAt;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setTocado(true);
    if (!cirugiaAt) return;
    onConfirmar(cirugiaAt);
  }

  return (
    <section className="flex flex-col">
      <Stepper paso={1} />

      <div className="flex flex-col gap-5 px-5 py-6">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="flex size-15 shrink-0 items-center justify-center rounded-full bg-accent text-heading shadow-glow-teal"
          >
            <MessageCircle className={ICONO.destacado} />
          </span>
          <div className="flex-1 rounded-tl-sm rounded-tr-xl rounded-br-xl rounded-bl-xl bg-brand-soft px-5 py-4">
            <h1 className="m-0 font-serif text-display-sm font-regular text-heading">
              {PACIENTE.onboarding.identidad.titulo}
            </h1>
            <p className="m-0 mt-1 text-lg text-body-strong">
              {PACIENTE.onboarding.identidad.presentacion}
            </p>
          </div>
        </div>

        <form className="flex flex-col gap-5" noValidate onSubmit={enviar}>
          <CampoGrande
            ayuda={PACIENTE.onboarding.identidad.nombreAyuda}
            etiqueta={PACIENTE.onboarding.identidad.nombreEtiqueta}
            name="alias"
            readOnly
            value={estado.alias}
          />

          <CampoGrande
            ayuda={PACIENTE.onboarding.identidad.cirugiaAyuda}
            error={faltaFecha ? PACIENTE.onboarding.identidad.cirugiaFalta : undefined}
            etiqueta={PACIENTE.onboarding.identidad.cirugiaEtiqueta}
            icono={CalendarCheck}
            name="cirugiaAt"
            onChange={(e) => setCirugiaAt(e.target.value)}
            type="date"
            value={cirugiaAt}
          />

          {error ? (
            <p className="m-0 text-md font-bold text-destructive-fg" role="alert">
              {error}
            </p>
          ) : null}

          <Boton cargando={enviando} type="submit">
            {PACIENTE.onboarding.continuar}
            <ArrowRight aria-hidden="true" className={ICONO.normal} />
          </Boton>
        </form>
      </div>
    </section>
  );
}
